import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface StatTileProps {
  readonly label: string;
  /** Already formatted. The composite does not know about locales, currencies or units. */
  readonly value: string;
  /** Context for the number: a period, a comparison, a target. */
  readonly caption?: string;
  /** A sparkline or a badge. Kept small: the number is the point. */
  readonly adornment?: ReactNode;
  readonly className?: string;
}

/**
 * One number, with enough context to mean something.
 *
 * A supervisor reads a row of these at a glance, so the number is the largest thing and the label
 * sits above it rather than below — the eye lands on the figure and then finds out what it is,
 * which is the order that lets a row be scanned.
 *
 * `tabular-nums`, so a column of tiles does not jiggle as the digits change. The value arrives
 * **already formatted**: a design-system component that knew about currencies would be a design
 * system that knew about this product.
 */
export function StatTile({ label, value, caption, adornment, className }: StatTileProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-(--space-xs) rounded-(--radius-md) border border-(--color-border)',
        'bg-(--color-surface) p-(--space-md)',
        className,
      )}
    >
      <span className="text-(length:--font-size-caption) text-(--color-text-secondary)">
        {label}
      </span>
      <span className="flex items-baseline gap-(--space-sm)">
        <span className="text-(length:--font-size-title) font-semibold tabular-nums text-(--color-text-primary)">
          {value}
        </span>
        {adornment}
      </span>
      {caption === undefined ? null : (
        <span className="text-(length:--font-size-caption) text-(--color-text-muted)">
          {caption}
        </span>
      )}
    </div>
  );
}
